/**
 * Rango Quantum — badge «esta config no es la de tu bot» (WP H-G).
 *
 * El motor de señales de rango opera SIEMPRE con la config de serie congelada
 * (rango_quantum_signal_tasks.py: los bots rango ignoran
 * indicator_engine_config — ver models/bot_config.py docstring): filtros
 * rsi_narrow + atr_min_median, 5×, TP 5/6×ATR 60/40, sin SL técnico,
 * time-stop 15 días. Si el usuario toca esos parámetros en el chart (o
 * apaga los filtros), lo pintado deja de ser lo que su bot opera. Este
 * helper compara la config del chart con esa referencia — más el leverage
 * del propio bot, el único parámetro de ejecución comparable 1:1 con lo
 * pintado (los TP del bot van en % de precio, no en ×ATR: no comparables).
 *
 * Datos: la lista de bots YA cargada por ChartPage (botsService.list()) en la
 * app vieja, y en la web pública (getquantum.app/app) la carga independiente
 * de useRangoBotDivergence.js sobre GET /api/public/bots (JWT chart efímero —
 * la web pública SÍ tiene auth; /snapshot, sin usuario, no tiene badge) —
 * sin endpoint nuevo en ninguna de las dos.
 */
import { CONFIG_SERIE_CONGELADA } from './engine.js'
import { normalizeBotSymbol } from '@/utils/normalizeSymbol'

// Matching símbolo chart↔bot con la MISMA normalización que el backend
// (_normalize_bot_symbol, signal_card_tasks.py:960 — port exacto en
// utils/normalizeSymbol.js): quita '/' y el sufijo ':QUOTE' de perpetuos.
// Bug (WP fix/hg-badge-symbol-norm): la normalización ad-hoc anterior solo
// quitaba no-alfanuméricos, así que el símbolo spot de la web ('XRP/USDT')
// no casaba con el del bot en futuros ('XRP/USDT:USDT' → XRPUSDTUSDT) y el
// badge nunca saltaba en la web.

/** Bot vivo del usuario que opera rango_quantum en ese símbolo+TF, o null. */
export function findLiveRangoBot(bots, chartSymbol, chartTimeframe) {
  if (!Array.isArray(bots)) return null
  const sym = normalizeBotSymbol(chartSymbol)
  return bots.find((b) =>
    b && b.status === 'active'
    && b.trigger_indicator === 'rango_quantum'
    && normalizeBotSymbol(b.symbol) === sym
    && String(b.trigger_timeframe || b.timeframe) === String(chartTimeframe)) ?? null
}

/**
 * null si no hay divergencia (o no hay bot vivo); si la hay,
 * { botName, diffs: [texto corto por campo] }.
 */
export function computeRangoBotDivergence(cfg, bots, chartSymbol, chartTimeframe) {
  const bot = findLiveRangoBot(bots, chartSymbol, chartTimeframe)
  if (!bot || !cfg) return null

  const frozen = CONFIG_SERIE_CONGELADA
  const diffs = []

  if (cfg.leverage !== bot.leverage) {
    diffs.push(`apalancamiento ${cfg.leverage}× (tu bot: ${bot.leverage}×)`)
  }
  const botFilters = [...frozen.filters].sort().join(',')
  const chartFilters = [...(cfg.filters ?? [])].sort().join(',')
  if (chartFilters !== botFilters) {
    diffs.push(chartFilters === ''
      ? 'filtros de producción desactivados (tu bot opera con rsi_narrow + atr_min_median)'
      : `filtros [${chartFilters}] (tu bot: [${botFilters}])`)
  }
  if (cfg.slMode !== frozen.slMode) {
    const detalle = cfg.slMode === 'pct' ? ` ${cfg.slPct}%` : cfg.slMode === 'atr' ? ` ${cfg.slAtr}×ATR` : ''
    diffs.push(`SL técnico${detalle} (tu bot opera sin SL técnico)`)
  }
  if (cfg.tp1DistanceAtr !== frozen.tpDistancesAtr[0]) {
    diffs.push(`TP1 ${cfg.tp1DistanceAtr}×ATR (tu bot: ${frozen.tpDistancesAtr[0]}×ATR)`)
  }
  if (cfg.tp2DistanceAtr !== frozen.tpDistancesAtr[1]) {
    diffs.push(`TP2 ${cfg.tp2DistanceAtr}×ATR (tu bot: ${frozen.tpDistancesAtr[1]}×ATR)`)
  }
  if (cfg.tp1WeightPct !== Math.round(frozen.tpWeights[0] * 100)) {
    diffs.push(`reparto TP1 ${cfg.tp1WeightPct}% (tu bot: ${Math.round(frozen.tpWeights[0] * 100)}%)`)
  }
  if (cfg.timeStopDays !== frozen.timeStopDays) {
    diffs.push(`time-stop ${cfg.timeStopDays}d (tu bot: ${frozen.timeStopDays}d)`)
  }

  if (diffs.length === 0) return null
  return { botName: bot.bot_name || 'tu bot', diffs }
}
